import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { CTAButton, mailto } from "@/components/site/CTAButton";
import { posts, formatDate } from "@/lib/posts";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title: "European Patent & Trademark Attorney for US Companies",
      },
      {
        name: "description",
        content:
          "European Patent Attorney for US companies with 50+ EPO prosecution matters and 10+ years of practice in physics, electronics and ICT.",
      },
      {
        name: "keywords",
        content:
          "European patent attorney, European patent lawyer, European trademark attorney, EU trademark attorney, EPO attorney, EUIPO attorney, patent attorney Europe, EP patent prosecution, UPC representation, European IP counsel for US companies",
      },
      {
        property: "og:title",
        content:
          "European Patent Attorney & Trademark Attorney for US Companies",
      },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "Karel Bauer advises US companies on European patent prosecution, with 50+ EPO matters, 10+ years of practice and a physics, electronics and ICT focus.",
      },
      { property: "og:url", content: "https://bauerip.com/" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content:
          "European Patent Attorney & Trademark Attorney for US Companies",
      },
      {
        name: "twitter:description",
        content:
          "Direct European patent prosecution counsel for US companies, grounded in 50+ EPO matters and technical practice in physics, electronics and ICT.",
      },
    ],
    links: [
      { rel: "preload", as: "image", href: "/karel-bauer-new.webp", type: "image/webp", fetchPriority: "high" },
      { rel: "canonical", href: "https://bauerip.com/" },
      { rel: "alternate", hrefLang: "en-US", href: "https://bauerip.com/" },
      { rel: "alternate", hrefLang: "en", href: "https://bauerip.com/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://bauerip.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": "https://bauerip.com/#karel-bauer",
              name: "Karel Bauer",
              jobTitle: [
                "European Patent Attorney",
                "European Trademark Attorney",
                "Representative before the Unified Patent Court",
              ],
              description:
                "European Patent Attorney with more than 10 years of practice and experience across more than 50 EPO prosecution matters in physics, electronics and ICT; qualified as a UPC representative.",
              url: "https://bauerip.com/",
              image: "https://bauerip.com/karel-bauer-new.webp",
              email: "mailto:docket@bauerip.com",
              telephone: "+420608785607",
              sameAs: ["https://www.linkedin.com/in/karelbauer/"],
              knowsLanguage: ["en", "cs", "de"],
              hasCredential: [
                {
                  "@type": "EducationalOccupationalCredential",
                  credentialCategory: "professional qualification",
                  name: "European Patent Attorney (entered on the EPO list of professional representatives)",
                  recognizedBy: {
                    "@type": "Organization",
                    name: "European Patent Office",
                    url: "https://www.epo.org/",
                  },
                },
                {
                  "@type": "EducationalOccupationalCredential",
                  credentialCategory: "professional qualification",
                  name: "European Trademark and Design Attorney (EUIPO list of representatives)",
                  recognizedBy: {
                    "@type": "Organization",
                    name: "European Union Intellectual Property Office",
                    url: "https://euipo.europa.eu/",
                  },
                },
                {
                  "@type": "EducationalOccupationalCredential",
                  credentialCategory: "professional qualification",
                  name: "Representative before the Unified Patent Court (UPC)",
                  recognizedBy: {
                    "@type": "Organization",
                    name: "Unified Patent Court",
                    url: "https://www.unified-patent-court.org/",
                  },
                },
              ],
              worksFor: { "@id": "https://bauerip.com/#practice" },
            },
            {
              "@type": "ProfessionalService",
              "@id": "https://bauerip.com/#practice",
              name: "Bauer IP — Karel Bauer",
              url: "https://bauerip.com/",
              image: "https://bauerip.com/karel-bauer-new.webp",
              email: "mailto:docket@bauerip.com",
              telephone: "+420608785607",
              priceRange: "$$$",
              founder: { "@id": "https://bauerip.com/#karel-bauer" },
              employee: { "@id": "https://bauerip.com/#karel-bauer" },
              sameAs: ["https://www.linkedin.com/in/karelbauer/"],
              address: {
                "@type": "PostalAddress",
                streetAddress: "124 City Road",
                addressLocality: "London",
                postalCode: "EC1V 2NX",
                addressCountry: "GB",
              },
              description:
                "European Patent Attorney and European Trade Mark Attorney advising US companies and law firms on EPO prosecution, EUIPO matters and UPC readiness.",
              areaServed: [
                { "@type": "Place", name: "European Union" },
                { "@type": "Place", name: "European Patent Convention states" },
                { "@type": "Place", name: "United Kingdom" },
                { "@type": "Country", name: "United States" },
              ],
              availableLanguage: ["English"],
              knowsAbout: [
                "European patent prosecution",
                "EPO opposition and appeals",
                "Unified Patent Court (UPC) representation",
                "Unitary Patent",
                "EU trade mark (EUTM) filing and prosecution",
                "EUIPO oppositions and cancellations",
                "PCT regional phase entry in Europe",
              ],
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "European IP services for US companies",
                itemListElement: [
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "European patent filing and EPO prosecution",
                      url: "https://bauerip.com/services/european-patent-attorney",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "EU trade mark filing, oppositions and licensing",
                      url: "https://bauerip.com/services/european-trademark-attorney",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Foreign associate services for US patent and trademark firms",
                      url: "https://bauerip.com/services/foreign-associate-for-us-firms",
                    },
                  },
                ],
              },
            },
          ],
        }),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const latestPosts = [...posts].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  ).slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* HERO with photo */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 -z-10 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, var(--foreground) 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
          <div className="mx-auto max-w-6xl px-6 pt-16 md:pt-24 pb-16 md:pb-24 grid gap-12 md:grid-cols-[1.4fr_1fr] items-center">
            <div>
              <p className="eyebrow mb-6">
                Karel Bauer · European Patent Attorney · European Trade Mark
                Attorney · UPC Representative
              </p>
              <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-foreground">
                European Patent &amp; Trade Mark Attorney for{" "}
                <span className="italic text-accent-text">US Companies</span>
              </h1>
              <p className="mt-8 max-w-xl text-lg md:text-xl text-muted-foreground leading-relaxed">
                European patent prosecution for US companies, handled directly
                by the attorney analysing the claims and preparing the EPO
                arguments. More than 50 prosecution matters over 10+ years,
                focused on physics, electronics and ICT.
              </p>
              <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {[
                  "50+ EPO prosecution matters",
                  "10+ years in European patent practice",
                  "EQE-qualified · UPC representative",
                ].map((c) => (
                  <li key={c} className="flex items-center gap-2">
                    <span className="text-accent-text">✓</span>
                    {c}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-3">
                <CTAButton
                  variant="accent"
                  href={mailto({
                    subject: "Consultation inquiry",
                    body: "Hello Karel,\n\nI'd like to discuss:\n\n— Company:\n— Website:\n— What I'm planning in Europe:\n",
                  })}
                >
                  Book a Consultation
                </CTAButton>
                <a
                  href="#services"
                  className="inline-flex items-center px-6 py-3 text-sm font-medium text-foreground hover:text-accent-text transition-colors"
                >
                  Explore services <span className="ml-2">↓</span>
                </a>
              </div>

            </div>
            <div className="relative">
              <div className="absolute -inset-4 -z-10 bg-accent/15 rounded-2xl rotate-2" />
              <div className="absolute -inset-4 -z-10 bg-primary/10 rounded-2xl -rotate-2" />
              <img
                src="/karel-bauer-new.webp"
                alt="Karel Bauer — European Patent Attorney"
                width="1380"
                height="1920"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="w-full rounded-2xl object-cover aspect-[4/5] shadow-xl ring-1 ring-border"
              />
              <div className="absolute -bottom-4 -left-4 bg-background border border-border rounded-full px-4 py-2 text-xs font-medium shadow-md">
                <span className="text-accent-text">●</span> Available for new
                mandates
              </div>
            </div>
          </div>
        </section>

        {/* DIVIDER ROUTER — Patents first */}
        <section id="services" className="border-y border-border bg-secondary/40">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
            <p className="eyebrow mb-3 text-center">Two practices</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground text-center max-w-2xl mx-auto leading-tight">
              Where do you need European IP support?
            </h2>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <Link
                to="/european-patents"
                className="group bg-background rounded-2xl p-8 md:p-10 border border-border hover:border-accent transition-all hover:shadow-lg flex flex-col"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="eyebrow">For US Deep-Tech</span>
                  <span className="text-xs text-muted-foreground bg-secondary px-3 py-1 rounded-full">
                    EPO · UPC
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-foreground leading-tight">
                  European Patents &amp; EPO Opposition Support
                </h3>
                <p className="mt-4 text-muted-foreground leading-relaxed flex-1">
                  Strategic filing and prosecution before the EPO for
                  technically demanding inventions in physics, electronics,
                  ICT and computer-implemented inventions.
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:gap-3 transition-all">
                  Enter patents{" "}
                  <span className="text-accent-text text-base">→</span>
                </span>
              </Link>

              <Link
                to="/eu-trademarks"
                className="group bg-background rounded-2xl p-8 md:p-10 border border-border hover:border-accent transition-all hover:shadow-lg flex flex-col"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="eyebrow">For US Brands</span>
                  <span className="text-xs text-muted-foreground bg-secondary px-3 py-1 rounded-full">
                    EUIPO · National
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-foreground leading-tight">
                  EU Trade Marks, Oppositions &amp; Licensing
                </h3>
                <p className="mt-4 text-muted-foreground leading-relaxed flex-1">
                  Franchise, licensing and distribution expansion into Europe —
                  without losing control of your brand or your royalty stream.
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:gap-3 transition-all">
                  Enter trade marks{" "}
                  <span className="text-accent-text text-base">→</span>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* WHO / WHY */}
        <section className="mx-auto max-w-6xl px-6 py-20 md:py-24 grid gap-12 md:grid-cols-2">
          <div>
            <p className="eyebrow mb-4">Who I am</p>
            <h2 className="font-serif text-2xl md:text-3xl text-foreground leading-tight">
              A European patent attorney with a physics background and my own
              established practice.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              I am a European Patent Attorney, European Trademark Attorney and
              a qualified representative before the Unified Patent Court. I
              have handled more than 50 EPO prosecution matters over 10+ years.
              My physics background supports direct work with inventions in
              physics, electronics and ICT.
            </p>
          </div>

          <div>
            <p className="eyebrow mb-4">Why work with me</p>
            <ul className="space-y-0">
              {[
                "The attorney analysing the claims also prepares the EPO response.",
                "More than 50 EPO prosecution matters over 10+ years.",
                "Technical focus in physics, electronics and ICT.",
                "UPC qualification clearly separated from EPO prosecution experience.",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex gap-5 border-t border-border py-4 last:border-b text-foreground"
                >
                  <span className="text-accent-text font-serif text-lg">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* END-TO-END — filing to UPC readiness */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
            <p className="eyebrow mb-4">End-to-end European patent strategy</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground max-w-3xl">From filing to UPC readiness, with one European Patent Attorney.</h2>
            <ol className="mt-10 grid gap-6 md:grid-cols-4">
              {[
                ["1. Filing", "Direct EPO filing or PCT European phase entry by month 31.", "/services/pct-european-phase-entry"],
                ["2. Prosecution", "Search opinion, examination reports and claim amendments before the EPO.", "/services/epo-office-action-response"],
                ["3. Grant & validation", "Unitary Patent or national validation in the countries that matter.", "/tools/european-patent-validation-costs"],
                ["4. UPC readiness", "Opt-out decisions and portfolio review. UPC qualification: CEIPI diploma and ERA/Maastricht certificate.", "/services/european-patent-attorney"],
              ].map(([t, d, href]) => (
                <li key={t} className="border-t border-border pt-5">
                  <h3 className="font-serif text-xl text-foreground">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{d}</p>
                  <a href={href} className="mt-3 inline-block text-sm text-accent-text underline underline-offset-4">Learn more →</a>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* SERVICES — internal links to commercial pages */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <p className="eyebrow mb-4">Services</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground leading-tight max-w-2xl">
              What US companies engage me for.
            </h2>
            <ul className="mt-10 grid gap-px bg-border border border-border md:grid-cols-2">
              {[
                {
                  to: "/services/european-patent-attorney" as const,
                  label: "European patent attorney for US companies",
                  desc: "EPO filing, PCT regional phase and prosecution led by an EQE-qualified attorney with 50+ prosecution matters.",
                },
                {
                  to: "/services/epo-office-action-response" as const,
                  label: "EPO office action response (Art. 94(3))",
                  desc: "Four-month deadlines, amendment strategy and how European arguments differ from USPTO practice.",
                },
                {
                  to: "/tools/epo-renewal-fees" as const,
                  label: "EPO renewal fees & annuity calculator",
                  desc: "Official 2026 fee schedule plus a calculator comparing classic EP renewals against the Unitary Patent.",
                },
                {
                  to: "/tools/european-patent-validation-costs" as const,
                  label: "European patent validation costs by country",
                  desc: "Translation requirements and per-country costs after grant, with a decision framework for market selection.",
                },
                {
                  to: "/services/european-trademark-attorney" as const,
                  label: "European trade mark attorney for US brands",
                  desc: "EUTM filing, oppositions and licensing support across the EUIPO and national offices.",
                },
                {
                  to: "/services/foreign-associate-for-us-firms" as const,
                  label: "Foreign associate for US patent firms",
                  desc: "Conflict-free EPO and EUIPO instruction handling, docketing and reporting formats US firms expect.",
                },
              ].map((s) => (
                <li key={s.to} className="bg-background p-8">
                  <Link
                    to={s.to}
                    className="font-serif text-xl text-foreground leading-snug hover:text-accent-text"
                  >
                    {s.label}
                  </Link>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    {s.desc}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* LATEST INSIGHTS */}
        <section className="border-t border-border bg-secondary/30">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <p className="eyebrow mb-4">Latest insights</p>
                <h2 className="font-serif text-3xl md:text-4xl text-foreground leading-tight max-w-2xl">
                  Notes on European IP for US companies.
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all"
              >
                View all insights <span className="text-accent-text">→</span>
              </Link>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {latestPosts.map((post) => (
                <article
                  key={post.slug}
                  className="group bg-background rounded-2xl p-6 border border-border hover:border-accent transition-all hover:shadow-lg flex flex-col"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="eyebrow text-xs">{post.category}</span>
                    <time
                      className="text-xs text-muted-foreground"
                      dateTime={post.date}
                    >
                      {formatDate(post.date)}
                    </time>
                  </div>
                  <h3 className="font-serif text-xl text-foreground leading-snug flex-1">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: post.slug }}
                      className="hover:text-accent-text transition-colors"
                    >
                      {post.title}
                    </Link>
                  </h3>
                  <p className="mt-4 text-muted-foreground leading-relaxed text-sm">
                    {post.excerpt}
                  </p>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:gap-3 transition-all"
                  >
                    Read article <span className="text-accent-text">→</span>
                  </Link>
                </article>
              ))}
            </div>

            <div className="mt-10 md:hidden">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all"
              >
                View all insights <span className="text-accent-text">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* CONTACT BAND */}
        <section className="relative overflow-hidden border-t border-border bg-primary text-primary-foreground">
          <div
            className="absolute -right-32 -top-32 w-96 h-96 rounded-full opacity-20"
            style={{ backgroundColor: "var(--accent)" }}
          />
          <div className="relative mx-auto max-w-6xl px-6 py-20 md:py-24 grid gap-10 md:grid-cols-[2fr_1fr] items-end">
            <div>
              <p
                className="eyebrow mb-4"
                style={{ color: "var(--accent)" }}
              >
                Get in touch
              </p>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight">
                Planning a move into Europe?
                <br />
                <span className="text-accent italic">
                  Let&rsquo;s talk before it becomes urgent.
                </span>
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              <CTAButton
                variant="accent"
                href={mailto({
                  subject: "Consultation inquiry",
                  body: "Hello Karel,\n\nI'd like to discuss:\n\n— Company:\n— Website:\n— What I'm planning in Europe:\n",
                })}
              >
                Book a Consultation
              </CTAButton>
              <a
                href="tel:+420608785607"
                className="text-sm text-primary-foreground/70 hover:text-primary-foreground text-center"
              >
                or call +420 608 785 607
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
